"use client";

import * as React from "react";
import { cn } from "@workspace/ui/lib/utils";
import type { TechStoryVideo } from "./tech-stories";

const MOBILE_MEDIA = "(max-width: 767px)";
// Share of the card that must be on screen for its video to play: the active slide, not the
// sliver of the next card peeking in at the edge.
const VISIBLE_RATIO = 0.6;

export type StoryVideoProps = {
  video: TechStoryVideo;
  /** This card is the selected slide (only it may gate the slider on buffering). */
  active: boolean;
  /**
   * Allowed to play (off for reduced motion: the poster stays). Plays only while the card is
   * mostly on screen (VISIBLE_RATIO), restarting from the beginning each time it comes into
   * view, whether by sliding to it or scrolling the page back to it; it pauses when it leaves.
   * Native autoplay would instead resume mid-way and also play the card peeking in at the edge.
   * Each entry calls play() again, so iOS Safari pausing a muted video that left the viewport
   * mid-slide doesn't leave it stuck.
   */
  autoPlay: boolean;
  /** Lazy gate: sources + poster are only attached once the section is near the viewport. */
  shouldLoad: boolean;
  onBufferingChange?: (buffering: boolean) => void;
  className?: string;
};

export function StoryVideo({
  video,
  active,
  autoPlay,
  shouldLoad,
  onBufferingChange,
  className,
}: StoryVideoProps) {
  const videoRef = React.useRef<HTMLVideoElement>(null);

  // Only the active card may gate autoplay on buffering.
  const reportBuffering = (buffering: boolean) => {
    if (active) onBufferingChange?.(buffering);
  };

  // <source> children appear only after shouldLoad flips; re-run resource selection.
  React.useEffect(() => {
    if (shouldLoad) videoRef.current?.load();
  }, [shouldLoad]);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldLoad || !autoPlay) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && entry.intersectionRatio >= VISIBLE_RATIO) {
          video.currentTime = 0;
          video.play().catch(() => {});
        } else if (!video.paused) {
          video.pause();
        }
      },
      { threshold: [0, VISIBLE_RATIO] },
    );
    observer.observe(video);
    return () => {
      observer.disconnect();
      video.pause();
    };
  }, [shouldLoad, autoPlay]);

  return (
    <video
      ref={videoRef}
      muted
      loop
      playsInline
      preload="none"
      disablePictureInPicture
      aria-hidden="true"
      tabIndex={-1}
      poster={shouldLoad ? video.poster : undefined}
      onWaiting={() => reportBuffering(true)}
      onPlaying={() => reportBuffering(false)}
      onCanPlay={() => reportBuffering(false)}
      onError={() => reportBuffering(false)}
      className={cn("pointer-events-none size-full object-cover", className)}
    >
      {shouldLoad ? (
        <>
          {video.mobileMp4 ? (
            <source
              src={video.mobileMp4}
              type="video/mp4"
              media={MOBILE_MEDIA}
            />
          ) : null}
          {/* Last candidate: its error means nothing is playable, so never stall the fill. */}
          <source
            src={video.mp4}
            type="video/mp4"
            onError={() => reportBuffering(false)}
          />
        </>
      ) : null}
    </video>
  );
}
